import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, MealType, RecipeListItem, isSubrecipe, isIngredientRecipe } from "../api/client";
import { MEAL_TYPE_LABELS } from "../constants";
import { Thumb } from "../components/Thumb";

const MEAL_TYPE_OPTIONS: { value: MealType | "all"; label: string }[] = [
  { value: "all", label: "Totes" },
  { value: "breakfast", label: MEAL_TYPE_LABELS.breakfast },
  { value: "lunch", label: MEAL_TYPE_LABELS.lunch },
  { value: "snack", label: MEAL_TYPE_LABELS.snack },
  { value: "subrecipe", label: MEAL_TYPE_LABELS.subrecipe },
];

export function RecipeLibrary() {
  const nav = useNavigate();
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [q, setQ] = useState("");
  const [mealType, setMealType] = useState<MealType | "all">("all");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [onlyFav, setOnlyFav] = useState(false);
  const [onlyIngredients, setOnlyIngredients] = useState(false);

  async function load() {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (mealType !== "all") params.set("mealType", mealType);
    if (selectedTag) params.set("tag", selectedTag);
    if (onlyFav) params.set("favorite", "true");
    if (onlyIngredients) params.set("ingredientRecipe", "true");
    const data = await api.get<RecipeListItem[]>(`/recipes?${params.toString()}`);
    setRecipes(data);
  }

  async function loadTags() {
    const data = await api.get<string[]>("/recipes/tags");
    setTags(data);
  }

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    load();
  }, [q, mealType, selectedTag, onlyFav, onlyIngredients]);

  useEffect(() => {
    function onChanged() { load(); }
    window.addEventListener("cookmenu:data-changed", onChanged);
    return () => window.removeEventListener("cookmenu:data-changed", onChanged);
  }, [q, mealType, selectedTag, onlyFav, onlyIngredients]);

  async function toggleFav(e: React.MouseEvent, r: RecipeListItem) {
    e.stopPropagation();
    if (r.favorite) await api.del(`/recipes/${r.id}/favorite`);
    else await api.post(`/recipes/${r.id}/favorite`);
    load();
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Capçalera */}
      <div className="page-head mb-0">
        <div>
          <h1>Receptes</h1>
          <div className="sub">
            {recipes.length} {recipes.length === 1 ? "recepta" : "receptes"}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => nav("/receptes/nova")}>+ Nova recepta</button>
      </div>

      {/* Cerca */}
      <div className="relative">
        <svg
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: "var(--faint)" }}
          aria-hidden
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nom o etiqueta…"
          className="w-full rounded-[13px] bg-surface py-2.5 pl-9 pr-4 text-[14px] text-foreground placeholder:text-faint focus:outline-none"
          style={{ border: "1.5px solid var(--border-strong)", transition: "border-color 0.15s" }}
        />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        {MEAL_TYPE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            className={`chip ${mealType === opt.value ? "active" : ""}`}
            onClick={() => setMealType(opt.value)}
          >
            {opt.label}
          </button>
        ))}
        <span className="mx-1 h-5 w-px" style={{ background: "var(--border-strong)" }} aria-hidden />
        {tags.length > 0 && (
          <>
            {tags.map((tag) => (
              <button
                key={tag}
                className={`chip ${selectedTag === tag ? "active" : ""}`}
                onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
              >
                {tag}
              </button>
            ))}
            <span className="mx-1 h-5 w-px" style={{ background: "var(--border-strong)" }} aria-hidden />
          </>
        )}
        <button
          className={`chip ${onlyFav ? "active" : ""}`}
          onClick={() => setOnlyFav(!onlyFav)}
        >
          ★ Preferits
        </button>
        <button
          className={`chip ${onlyIngredients ? "active" : ""}`}
          onClick={() => setOnlyIngredients(!onlyIngredients)}
        >
          🍎 Ingredients
        </button>
      </div>

      {/* Graella */}
      {recipes.length === 0 ? (
        <div className="empty">Cap recepta coincideix amb la cerca.</div>
      ) : (
        <div className="recipe-grid">
          {recipes.map((r) => {
            const target = isIngredientRecipe(r)
              ? `/ingredients/${r.ingredientId}`
              : `/receptes/${r.id}`;
            return (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              className="recipe-card"
              onClick={() => nav(target)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  nav(target);
                }
              }}
            >
              <Thumb
                name={r.name}
                imageUrl={r.imageUrl}
                meal={r.mealType}
                className="rc-thumb"
                size={48}
              >
                {isIngredientRecipe(r) ? (
                  <span
                    className="absolute left-2 top-2 rounded-lg px-2 py-0.5 text-xs font-semibold shadow-card-sm"
                    style={{ background: "rgba(255,255,255,.92)", color: "var(--foreground)" }}
                  >
                    Ingredient
                  </span>
                ) : isSubrecipe(r.mealType) && (
                  <span
                    className="absolute left-2 top-2 rounded-lg px-2 py-0.5 text-xs font-semibold shadow-card-sm"
                    style={{ background: "rgba(255,255,255,.92)", color: "var(--foreground)" }}
                  >
                    Subrecepta
                  </span>
                )}
                <button
                  className={`rnd rc-fav ${r.favorite ? "on" : ""}`}
                  onClick={(e) => toggleFav(e, r)}
                  aria-label={r.favorite ? "Treu de preferits" : "Marca preferida"}
                >
                  {r.favorite ? "★" : "☆"}
                </button>
              </Thumb>

              <div className="rc-body">
                <h3 className="rc-name">{r.name}</h3>
                <div className="rc-meta">
                  <span className="tag accent">{MEAL_TYPE_LABELS[r.mealType]}</span>
                  {r.prepTimeMin ? (
                    <span
                      className="inline-flex items-center gap-1 text-[13px] font-bold"
                      style={{ color: "var(--muted)" }}
                    >
                      ⏱ {r.prepTimeMin}′
                    </span>
                  ) : null}
                </div>
                {r.tags.length > 0 && (
                  <div className="rc-tags">
                    {r.tags.slice(0, 3).map((t) => (
                      <span key={t} className="tag">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
